import type { ErrorInfo } from "react";
import { sentryBaseOptions } from "@/shared/lib/sentry/options";

type SentryModule = typeof import("@sentry/react");

let sentryPromise: Promise<SentryModule> | null = null;

/**
 * Carrega e inicializa o Sentry sob demanda: o SDK (~30 kB gzip) fica fora do
 * caminho crítico. Sem DSN (ex.: dev local) nada é baixado.
 */
export function loadSentry(): Promise<SentryModule> | null {
  if (!sentryBaseOptions.dsn) return null;
  sentryPromise ??= import("@sentry/react").then((Sentry) => {
    Sentry.init({
      ...sentryBaseOptions,
      // Ruído do próprio navegador, não indica bug do sistema.
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
      ],
    });
    return Sentry;
  });
  return sentryPromise;
}

/** Handler do createRoot: garante o SDK carregado antes de reportar. */
export function reportReactError(error: unknown, errorInfo: ErrorInfo): void {
  void loadSentry()?.then((Sentry) =>
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack ?? "" } } }),
  );
}

/** Agenda o carregamento para quando o navegador estiver ocioso (após o primeiro paint). */
export function scheduleSentry(): void {
  const start = () => void loadSentry();
  if ("requestIdleCallback" in window) window.requestIdleCallback(start, { timeout: 4000 });
  else setTimeout(start, 2000);
}
